/**
 * 엑셀 데이터 중복 검사 유틸리티
 * - 여러 ExcelUpload.vue 컴포넌트에서 공통으로 사용
 * - 업로드된 엑셀 데이터가 기존 데이터와 중복되는지 검사
 */

// 중복 검사 결과를 담은 객체 반환 함수
/**
 * 엑셀 데이터 중복 검사를 수행하는 함수
 *
 * @param {Object} options - 중복 검사에 필요한 옵션
 * @param {Array} options.excelData - 검사할 엑셀 데이터
 * @param {Array} options.existDataList - 중복 검사 대상 필드 목록
 * @param {Array} options.tableDataValue - 테이블 데이터 값 목록
 * @param {number} options.projectId - 프로젝트 ID
 * @param {number} options.boardId - 보드 ID
 * @param {Function} options.dispatch - Vuex 스토어의 dispatch 함수
 * @param {Object} options.lastProcessedData - 마지막으로 처리한 데이터 (중복 호출 방지용)
 * @returns {Promise<{hasError: boolean, data: Array}>} - 중복 검사 결과 객체
 */
export async function checkExcelDataDuplicates(options) {
  const {
    excelData,
    existDataList,
    tableDataValue,
    projectId,
    boardId,
    dispatch,
    lastProcessedData
  } = options;

  // 데이터가 없으면 처리하지 않음
  if (!excelData || excelData.length === 0) {
    return { hasError: false, data: excelData };
  }

  // 이전에 처리한 데이터와 현재 데이터를 비교해서 변경이 없으면 중단
  if (lastProcessedData && JSON.stringify(lastProcessedData) === JSON.stringify(excelData)) {
    return { hasError: false, data: excelData };
  }

  // 중복 데이터 검사 대상 필드가 없으면 처리하지 않음
  if (!existDataList || existDataList.length < 1) {
    return { hasError: false, data: excelData };
  }

  try {
    const metaKeys = [];
    existDataList.forEach((key) => {
      metaKeys.push(`body.subInfo.${key}`);
    });

    // 기존 데이터를 저장할 배열 초기화
    const existingData = [];

    // 1000개씩 불러오는 페이지네이션 처리
    let currentPage = 1;
    let totalPages = 1;

    do {
      const response = await dispatch("board/LOAD_POST_LIST", {
        projectId,
        boardId,
        page: currentPage,
        perPage: 1000,
        withMeta: true,
        onlyMeta: true,
        metaKeys,
      });

      // 총 페이지 수 계산
      if (currentPage === 1 && response.total) {
        totalPages = Math.ceil(response.total / 1000);
      }

      // 기존 데이터 형식화
      if (response && response.data && response.data.length > 0) {
        response.data.forEach(post => {
          if (post.postContents && post.postContents.body && post.postContents.body.subInfo) {
            const subInfo = post.postContents.body.subInfo;
            const fieldInfo = {
              id: post.field_id,
              field_name: post.field && post.field.field_name ? post.field.field_name : ''
            };

            // 중복 체크에 필요한 정보만 추출
            const checkData = {
              field: fieldInfo,
              subInfo: {}
            };

            existDataList.forEach(key => {
              checkData.subInfo[key] = subInfo[key] || '';
            });

            existingData.push(checkData);
          }
        });
      }

      currentPage++;
    } while (currentPage <= totalPages);

    // 에러 발생 여부를 추적하기 위한 변수
    let hasErrorOccurred = false;

    // 엑셀 데이터와 기존 데이터 비교하여 중복 체크
    if (excelData && excelData.length > 0) {
      excelData.forEach((item) => {
        const values = item.values;
        if (values && values.length > 0) {
          // 기존 중복 오류 메시지가 있다면 먼저 제거 (기본적으로 중복이 아니라고 가정)
          // 오류 메시지가 1번 인덱스(사업장명)에 설정되어 있으므로, 해당 위치의 오류 메시지를 체크
          if (item.errorMSG && item.errorMSG[1] && item.errorMSG[1].includes('이미 등록된')) {
            item.errorMSG[1] = '';
          }

          // 엑셀 데이터에서 필드값과 중복 체크 대상 정보 추출
          const fieldInfo = values[1]; // 사업장명(field) 정보

          // 중복 체크에 필요한 데이터 추출
          const checkData = {
            field: fieldInfo,
            subInfo: {}
          };

          existDataList.forEach((key) => {
            // tableDataValue와 tableDataType의 인덱스 매핑
            const valueIndex = tableDataValue.indexOf(key);
            if (valueIndex !== -1) {
              // 실제 values 배열에서의 인덱스는 +2 (No.와 사업장명 컬럼 이후부터)
              checkData.subInfo[key] = values[valueIndex + 2] || '';
            }
          });

          // 중복 검사
          const isDuplicate = existingData.some(existItem => {
            // 필드 ID가 같은지 확인
            if (existItem.field.id !== checkData.field.id) {
              return false;
            }

            // 모든 체크 대상 필드가 같은지 확인
            return existDataList.every(key => existItem.subInfo[key] === checkData.subInfo[key] &&
              existItem.subInfo[key] !== '' // 빈 값은 중복으로 처리하지 않음
            );
          });

          // 중복된 경우 에러 메시지 추가 - 구분 필드 값 사용하여 동적 메시지 생성
          if (isDuplicate) {
            // 구분 필드 값 가져오기 (tableDataValue 배열에서 "구분"의 인덱스 찾기)
            const typeIndex = tableDataValue.indexOf("구분");
            let itemType = "항목";

            if (typeIndex !== -1) {
              // 실제 values 배열에서의 인덱스는 +2 (No.와 사업장명 컬럼 이후부터)
              itemType = values[typeIndex + 2] || "항목";
            }

            item.errorMSG[1] = `이미 등록된 ${itemType} 정보가 있습니다.`;
            hasErrorOccurred = true;
          }
        }
      });
    }

    return {
      hasError: hasErrorOccurred,
      data: excelData
    };
  } catch (error) {
    console.error('중복 데이터 검사 중 오류 발생:', error);
    return {
      hasError: false,
      data: excelData
    };
  }
}
